import React, { useState, useMemo, useCallback } from 'react';
import { useFinanceDataContext } from '../../contexts/FinanceDataContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { formatSmartCurrency } from '../../utils/formatCurrency';
import {
  formatBalanceData,
  formatIncomeData,
  formatExpenseData,
  formatLiabilityData,
  sortData,
  filterData,
} from '../../utils/ledgerUtils';
import useDebtData from '../../hooks/useDebtData';
import { useTheme } from '../../contexts/ThemeContext';

const TABS = [
  { key: 'balances', label: 'Balances', emoji: '🏦' },
  { key: 'income', label: 'Income', emoji: '💰' },
  { key: 'expenses', label: 'Expenses', emoji: '💳' },
  { key: 'liabilities', label: 'Liabilities', emoji: '📋' },
];

const SORT_OPTIONS = [
  { value: 'amount-high', label: 'Amount (High → Low)' },
  { value: 'amount-low', label: 'Amount (Low → High)' },
  { value: 'name-az', label: 'Name (A → Z)' },
  { value: 'name-za', label: 'Name (Z → A)' },
];

const LedgerPage = () => {
  const { cashFlowItems, balances } = useFinanceDataContext();
  const { liabilities } = useDebtData();
  const { currencyCode } = useCurrency();
  const { theme } = useTheme();

  const [activeTab, setActiveTab] = useState('balances');
  const [sortBy, setSortBy] = useState('amount-high');
  const [search, setSearch] = useState('');

  const fmt = useCallback(
    (amount, mode = 'long') => formatSmartCurrency(amount, { mode, currencyCode }),
    [currencyCode]
  );

  // Format data based on active tab
  const rawData = useMemo(() => {
    switch (activeTab) {
      case 'balances':
        return formatBalanceData(balances);
      case 'income':
        return formatIncomeData(cashFlowItems);
      case 'expenses':
        return formatExpenseData(cashFlowItems);
      case 'liabilities':
        return formatLiabilityData(liabilities);
      default:
        return [];
    }
  }, [activeTab, balances, cashFlowItems, liabilities]);

  // Apply sorting and filtering
  const displayData = useMemo(() => {
    let filtered = filterData(rawData, { search: search.trim() });
    return sortData(filtered, sortBy);
  }, [rawData, sortBy, search]);

  // Tab totals
  const tabTotal = useMemo(() => {
    return displayData.reduce((sum, item) => sum + (item.amount || 0), 0);
  }, [displayData]);

  return (
    <div style={{ padding: '2rem', maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>Ledger</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: '1rem', overflowX: 'auto' }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setSearch(''); }}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: 8,
              border: activeTab === tab.key ? '1px solid #2563eb' : '1px solid #e5e7eb',
              backgroundColor: activeTab === tab.key ? '#eff6ff' : '#fff',
              color: activeTab === tab.key ? '#2563eb' : '#374151',
              fontWeight: activeTab === tab.key ? 600 : 400,
              fontSize: '0.8rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span>{tab.emoji}</span> {tab.label}
          </button>
        ))}
      </div>

      {/* Controls: search + sort + total */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: 160,
            padding: '0.5rem 0.75rem',
            border: `1px solid ${theme.borderColor}`,
            borderRadius: 8,
            fontSize: '0.8rem',
            outline: 'none',
          }}
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            padding: '0.5rem 0.75rem',
            border: `1px solid ${theme.borderColor}`,
            borderRadius: 8,
            fontSize: '0.8rem',
            backgroundColor: theme.backgroundColor,
            cursor: 'pointer',
            outline: 'none',
          }}
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: theme.textColor, whiteSpace: 'nowrap' }}>
          Total: {fmt(tabTotal)}
        </div>
      </div>

      {/* Data rows */}
      <div style={{ backgroundColor: theme.backgroundColor, border: `1px solid ${theme.borderColor}`, borderRadius: 12, overflow: 'hidden' }}>
        {displayData.length > 0 ? (
          displayData.map((item, i) => (
            <LedgerRow key={item.id || i} item={item} fmt={fmt} activeTab={activeTab} theme={theme} />
          ))
        ) : (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: theme.tertiaryText, fontSize: '0.875rem' }}>
            {search ? `No results for "${search}"` : 'No data yet'}
          </div>
        )}
      </div>

      <div style={{ fontSize: '0.7rem', color: theme.tertiaryText, marginTop: '0.75rem', textAlign: 'center' }}>
        {displayData.length} item{displayData.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

const LedgerRow = ({ item, fmt, activeTab, theme }) => {
  const isNegative = activeTab === 'liabilities' || activeTab === 'expenses';
  const amountColor = isNegative ? '#dc2626' : '#059669';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0.75rem 1rem',
        borderBottom: `1px solid ${theme.lightBorder}`,
        transition: 'background-color 0.1s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
    >
      {/* Icon */}
      <span style={{ fontSize: '1.125rem', marginRight: 10, minWidth: 28, textAlign: 'center' }}>
        {item.icon || '📄'}
      </span>

      {/* Title + subtitle */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 500, color: theme.textColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.title}
        </div>
        <div style={{ fontSize: '0.75rem', color: theme.tertiaryText }}>
          {item.subtitle}
          {item.metadata?.apy > 0 && ` · ${item.metadata.apy}% APY`}
          {item.metadata?.interestRate > 0 && ` · ${item.metadata.interestRate}% APR`}
        </div>
      </div>

      {/* Amount */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: amountColor }}>
          {fmt(item.amount)}
        </div>
        {item.metadata?.changeAmount !== undefined && item.metadata.changeAmount !== 0 && (
          <div style={{ fontSize: '0.65rem', color: item.metadata.changeAmount > 0 ? '#059669' : '#dc2626' }}>
            {item.metadata.changeAmount > 0 ? '+' : ''}{fmt(item.metadata.changeAmount, 'short')}
          </div>
        )}
      </div>
    </div>
  );
};

export default LedgerPage;
